import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Collapse, Divider,
  IconButton, ListSubheader, MenuItem, Paper, Stack, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BoltIcon from '@mui/icons-material/Bolt';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { CMButton } from '../../containers/styledComponents';

// Friendly, plain-language option sets. The underlying spec values are
// unchanged (pct/abs/sigma, step/ramp, direction ±1) — only labels are human.
const UNIT_OPTIONS = [
  { value: 'pct', label: 'percent (%)', short: '%' },
  { value: 'abs', label: 'absolute units', short: ' (abs)' },
  { value: 'sigma', label: 'standard deviations (σ)', short: 'σ' },
];
const UNIT_SHORT = Object.fromEntries(UNIT_OPTIONS.map((u) => [u.value, u.short]));

const SHAPE_OPTIONS = [
  { value: 'step', label: 'all at once' },
  { value: 'ramp', label: 'gradually' },
];

const EMPTY_SPEC = { name: '', overrides: [], events: [] };

const numOr = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

// Raw keys read like plumbing ("company.fuel_cost") — turn them into words
// for any example we have to build client-side. The LLM-written examples
// stored on the goal (``scenario_examples``) are preferred; this is only the
// fallback for goals that haven't generated them yet.
const humanizeKey = (key) => {
  if (!key) return '';
  const last = String(key).split('.').pop();
  return last.replace(/__/g, ' ').replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
};

const sentenceCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Fallback example prompts built from THIS goal's real drivers — same
// {label, prompt, kind} shape as the stored LLM examples so the UI renders
// one way. They exist purely to show the phrasing the parser understands.
const buildFallbackSuggestions = (signalDrivers, eventStreams) => {
  const out = [];
  const a = signalDrivers[0] && humanizeKey(signalDrivers[0].key);
  const b = signalDrivers[1] && humanizeKey(signalDrivers[1].key);
  if (a) {
    out.push({
      label: `${sentenceCase(a)} rises 10%`,
      prompt: `What if ${a} rises 10%?`,
      kind: 'increase',
    });
  }
  if (b) {
    out.push({
      label: `${sentenceCase(b)} drops 5%`,
      prompt: `What if ${b} drops 5%?`,
      kind: 'decrease',
    });
  }
  if (eventStreams[0]) {
    const ev = humanizeKey(eventStreams[0]);
    out.push({
      label: `A negative ${ev} shock`,
      prompt: `What if a negative ${ev} shock hits?`,
      kind: 'event',
    });
  }
  return out.slice(0, 3);
};

// Placeholder drawn from the goal's own top driver so the empty state teaches
// real phrasing instead of a made-up example.
const buildFallbackPlaceholder = (signalDrivers) => {
  const d = signalDrivers[0];
  if (d) return `What if ${humanizeKey(d.key)} rises 10%?`;
  return 'What if a driver moves? e.g. price up 10%';
};

const KIND_ICONS = {
  increase: TrendingUpIcon,
  decrease: TrendingDownIcon,
  event: BoltIcon,
};

/* One example prompt as a soft pill: kind icon + human label. Clicking runs
   the full prompt through the NL parser, exactly like typing it. */
const ExampleChip = ({ example, onClick, disabled }) => {
  const Icon = KIND_ICONS[example.kind] || AutoAwesomeIcon;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      sx={(t) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1.25,
        py: 0.55,
        borderRadius: 999,
        border: `1px solid ${alpha(t.palette.primary.main, 0.25)}`,
        bgcolor: alpha(t.palette.primary.main, 0.05),
        color: t.palette.text.primary,
        fontFamily: t.typography.fontFamily,
        fontSize: 12.5,
        lineHeight: 1.4,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'border-color 120ms ease, background-color 120ms ease',
        '&:hover': disabled ? {} : {
          borderColor: alpha(t.palette.primary.main, 0.6),
          bgcolor: alpha(t.palette.primary.main, 0.1),
        },
      })}
    >
      <Icon sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />
      {example.label}
    </Box>
  );
};

const driverMeta = (d) => {
  if (!d) return '';
  const bits = [];
  if (d.current_value != null) {
    bits.push(`now ≈ ${d.current_value}${d.unit ? ` ${d.unit}` : ''}`);
  }
  if (d.direction) bits.push(`${d.direction} effect`);
  if (d.tier && d.tier !== 'validated' && d.confidence != null) {
    bits.push(`confidence ≈ ${Math.round(d.confidence * 100)}%`);
  }
  if (d.lifecycle === 'weakening') bits.push('weakening');
  return bits.join('  ·  ');
};

const PressureDot = ({ pressure }) => {
  if (pressure === 'upward') return <TrendingUpIcon sx={{ fontSize: 15, color: 'warning.main' }} />;
  if (pressure === 'downward') return <TrendingDownIcon sx={{ fontSize: 15, color: 'success.main' }} />;
  return null;
};

const overrideClause = (o, signalUnit) => {
  if (!o.key) return null;
  const neg = String(o.amount).trim().startsWith('-');
  const mag = Math.abs(numOr(o.amount));
  const mode = o.mode || 'pct';
  // Absolute moves read in the signal's real unit when known
  // ("fuel_cost up 5 $/MWh"); % and σ stay symbolic.
  const unit = (mode === 'abs' && signalUnit)
    ? ` ${signalUnit}` : (UNIT_SHORT[mode] || '');
  const amt = unit === '%' ? `${mag}%` : `${mag}${unit}`;
  const tail = (o.shape === 'ramp') ? ', gradually' : '';
  return `${o.key} ${neg ? 'down' : 'up'} ${amt}${tail}`;
};
const eventClause = (e) => {
  if (!e.stream) return null;
  const sign = numOr(e.direction, 1) < 0 ? 'negative' : 'positive';
  const mag = numOr(e.magnitude, 1);
  const when = numOr(e.at_offset_bins, 0) > 0
    ? ` at step ${numOr(e.at_offset_bins, 0)}` : '';
  return `${sign} ${e.stream} shock${mag !== 1 ? ` ×${mag}` : ''}${when}`;
};

/**
 * The natural-language prompt. Minimal on purpose: a quiet single-line field
 * (grows only when the user types more) with a small send affordance — it
 * should read like a search box, not a hero banner. The placeholder is built
 * from the goal's own top driver so it doubles as a real, runnable example.
 */
const NlPrompt = ({ value, onChange, onSubmit, isLoading, placeholder }) => {
  const canSend = !isLoading && (value || '').trim().length > 0;
  const onKeyDown = (e) => {
    if (e.keyCode === 13 && !e.shiftKey) {
      e.preventDefault();
      if (canSend) onSubmit();
    }
  };
  return (
    <Box
      sx={(t) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: '6px 6px 6px 12px',
        borderRadius: 2,
        bgcolor: 'background.default',
        border: `1px solid ${t.palette.divider}`,
        transition: 'border-color 120ms',
        '&:focus-within': { borderColor: t.palette.primary.main },
      })}
    >
      <AutoAwesomeIcon sx={{ color: 'text.disabled', fontSize: 16, flexShrink: 0 }} />
      <Box
        component={TextareaAutosize}
        aria-label="Describe a what-if"
        minRows={1}
        maxRows={4}
        placeholder={placeholder}
        value={value || ''}
        disabled={isLoading}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        sx={(t) => ({
          flex: 1,
          resize: 'none',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: t.palette.text.primary,
          fontFamily: t.typography.fontFamily,
          fontSize: 14,
          lineHeight: 1.5,
          padding: '4px 0',
          '&::placeholder': { color: t.palette.text.secondary, opacity: 0.7 },
          '&:disabled': { opacity: 0.6 },
        })}
      />
      <IconButton
        onClick={onSubmit}
        disabled={!canSend}
        size="small"
        aria-label="Run from description"
        sx={(t) => ({
          width: 28, height: 28, borderRadius: '50%', flexShrink: 0, alignSelf: 'flex-end',
          bgcolor: canSend ? t.palette.primary.main : 'transparent',
          color: canSend ? t.palette.primary.contrastText : t.palette.text.disabled,
          '&:hover': { bgcolor: canSend ? t.palette.primary.dark : alpha(t.palette.primary.main, 0.1) },
          '&.Mui-disabled': { bgcolor: 'transparent', color: t.palette.text.disabled },
          transition: 'background-color 120ms',
        })}
      >
        {isLoading
          ? <CircularProgress size={14} thickness={5} sx={{ color: 'inherit' }} />
          : <ArrowUpwardIcon sx={{ fontSize: 16 }} />}
      </IconButton>
    </Box>
  );
};

/**
 * Scenario builder (plan Step 15). Minimal-first: only the natural-language
 * prompt + suggestion chips show until the user opens the manual editor or a
 * parse fills the spec. The manual editor reads as a sentence (Driver ▸
 * increase/decrease ▸ amount ▸ unit ▸ shape) with rich dropdowns, and a live
 * plain-English summary precedes the run. Fully controlled.
 */
const ScenarioBuilder = ({
  drivers, predictiveDrivers, value, onChange, onRun, isRunning, creditCost,
  onParse, isParsing, scenarioExamples,
}) => {
  const spec = value || EMPTY_SPEC;
  const name = spec.name || '';
  const overrides = useMemo(() => spec.overrides || [], [spec.overrides]);
  const events = useMemo(() => spec.events || [], [spec.events]);

  const [nlText, setNlText] = useState('');
  const [manualOpen, setManualOpen] = useState(false);

  // Validated tier — deduped by key (a key can carry several edges /
  // channels; the picker offers each driver once). Drivers the engine
  // can't actually move (BE stamps ``simulatable: false`` — e.g. columns
  // outside a foundation adapter's channel plan) are skipped: offering
  // them would only produce zero-delta runs with a warning.
  const signalDrivers = useMemo(() => {
    const seen = new Set();
    return (drivers || []).filter((d) => {
      if (d.kind === 'event' || !d.key || seen.has(d.key)) return false;
      if (d.simulatable === false) return false;
      seen.add(d.key);
      return true;
    });
  }, [drivers]);
  // Predictive tier (suggestive / candidate / leading) — every extra
  // driver the engine can actually simulate (BE stamps ``simulatable``:
  // serving-segment columns run model-exact, validated chain sources
  // chain-estimated). Keys already in the validated tier are skipped.
  const suggestiveDrivers = useMemo(() => {
    const validated = new Set(signalDrivers.map((d) => d.key));
    const seen = new Set();
    return (predictiveDrivers || []).filter((d) => {
      if (d.kind === 'event' || !d.key) return false;
      if (d.simulatable === false) return false;
      if (validated.has(d.key) || seen.has(d.key)) return false;
      seen.add(d.key);
      return true;
    });
  }, [predictiveDrivers, signalDrivers]);
  const allSignalDrivers = useMemo(
    () => [...signalDrivers, ...suggestiveDrivers],
    [signalDrivers, suggestiveDrivers]);
  const eventDrivers = useMemo(
    () => (drivers || []).filter((d) => d.kind === 'event'), [drivers]);
  const eventStreams = useMemo(
    () => [...new Set(eventDrivers.map((d) => d.key))], [eventDrivers]);
  const driverByKey = useMemo(() => {
    const m = {};
    allSignalDrivers.forEach((d) => { if (d.key && !m[d.key]) m[d.key] = d; });
    return m;
  }, [allSignalDrivers]);
  // Prefer the LLM-written examples stored on the goal (generated after each
  // discovery run) — natural phrasing, human names. Fall back to key-derived
  // templates until they exist.
  const storedExamples = scenarioExamples?.examples;
  const suggestions = useMemo(() => {
    if (Array.isArray(storedExamples) && storedExamples.length > 0) {
      return storedExamples
        .filter((e) => e && e.label && e.prompt)
        .slice(0, 4);
    }
    return buildFallbackSuggestions(
      signalDrivers.length ? signalDrivers : suggestiveDrivers, eventStreams);
  }, [storedExamples, signalDrivers, suggestiveDrivers, eventStreams]);
  const placeholder = scenarioExamples?.placeholder
    || buildFallbackPlaceholder(
      signalDrivers.length ? signalDrivers : suggestiveDrivers);

  const hasContent = (overrides.length + events.length) > 0;
  // A parse (or any seeded content) reveals the editor so users can fine-tune.
  useEffect(() => { if (hasContent) setManualOpen(true); }, [hasContent]);
  const showManual = manualOpen || hasContent;

  const setName = (v) => onChange({ ...spec, name: v });
  const addOverride = () => onChange({
    ...spec,
    overrides: [...overrides, {
      key: allSignalDrivers[0]?.key || '', mode: 'pct', amount: '10', shape: 'step',
    }],
  });
  const patchOverride = (i, patch) => onChange({
    ...spec, overrides: overrides.map((o, j) => (j === i ? { ...o, ...patch } : o)),
  });
  const removeOverride = (i) => onChange({
    ...spec, overrides: overrides.filter((_, j) => j !== i),
  });
  const addEvent = () => onChange({
    ...spec,
    events: [...events, { stream: eventStreams[0] || '', magnitude: 1, direction: 1 }],
  });
  const patchEvent = (i, patch) => onChange({
    ...spec, events: events.map((e, j) => (j === i ? { ...e, ...patch } : e)),
  });
  const removeEvent = (i) => onChange({
    ...spec, events: events.filter((_, j) => j !== i),
  });

  const setOverrideDir = (i, o, dir) => {
    const mag = String(o.amount ?? '').trim().replace(/^-/, '');
    patchOverride(i, { amount: (dir === -1 ? '-' : '') + mag });
  };
  const setOverrideMag = (i, o, magRaw) => {
    const neg = String(o.amount ?? '').trim().startsWith('-');
    const mag = String(magRaw).replace(/-/g, '');
    patchOverride(i, { amount: (neg ? '-' : '') + mag });
  };

  const clauses = useMemo(() => [
    ...overrides.map((o) => overrideClause(o, driverByKey[o.key]?.unit)),
    ...events.map(eventClause),
  ].filter(Boolean), [overrides, events, driverByKey]);

  const canRun = !isRunning && !isParsing
    && (overrides.some((o) => o.key) || events.some((e) => e.stream));

  const parse = (text) => {
    const t = (text || '').trim();
    if (!t || isParsing) return;
    onParse(t);
  };
  const onSuggestion = (text) => { setNlText(text); parse(text); };
  const openManual = () => { setManualOpen(true); if (!hasContent) addOverride(); };
  const closeManual = () => { setManualOpen(false); onChange({ ...spec, overrides: [], events: [] }); };

  const run = () => {
    onRun({
      name: name || clauses.join(' · ').slice(0, 80) || 'Scenario',
      overrides: overrides.filter((o) => o.key).map((o) => ({ ...o, amount: numOr(o.amount) })),
      events: events.filter((e) => e.stream).map((e) => {
        const out = {
          ...e, magnitude: numOr(e.magnitude, 1), direction: numOr(e.direction, 1),
        };
        const offset = numOr(e.at_offset_bins, 0);
        // Only ship a timing when it says something (0 = default "now").
        if (offset > 0) out.at_offset_bins = Math.round(offset);
        else delete out.at_offset_bins;
        return out;
      }),
    });
  };

  // One menu row per driver — shared by both tiers. Suggestive-tier rows
  // carry an outlined tier chip so the honesty labeling survives into the
  // picker ("suggestive", "leading", "candidate"); chain sources say so.
  const driverMenuItem = (d) => (
    <MenuItem key={d.edge_id || d.key} value={d.key} sx={{ display: 'block', py: 0.75 }}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <PressureDot pressure={d.pressure} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{d.key}</Typography>
        {d.lifecycle === 'weakening' && (
          <Chip size="small" variant="outlined" color="warning" label="weakening"
            sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: 10 } }} />
        )}
        {d.tier && d.tier !== 'validated' && (
          <Chip size="small" variant="outlined" color="default" label={d.tier}
            sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: 10 } }} />
        )}
        {d.mechanism === 'chain-estimated' && (
          <Chip size="small" variant="outlined" color="info" label="via chain"
            sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: 10 } }} />
        )}
        {Array.isArray(d.via) && d.via.length > 0 && (
          // The model was trained on a feature derived from this signal
          // (e.g. its lag) rather than the raw level; the engine
          // recomputes those carriers on the counterfactual.
          <Chip size="small" variant="outlined" color="info"
            label={`via ${d.via[0]}${d.via.length > 1 ? ` +${d.via.length - 1}` : ''}`}
            sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: 10 } }} />
        )}
      </Stack>
      {driverMeta(d) && (
        <Typography variant="caption" color="text.secondary">{driverMeta(d)}</Typography>
      )}
    </MenuItem>
  );

  // One override card reads as a sentence: driver on top, then
  // "[↑ up | ↓ down]  by [10] [%]  [all at once]". No labeled dropdown per
  // word — the widgets themselves carry the meaning.
  const renderOverride = (o, i) => {
    const sel = driverByKey[o.key];
    const neg = String(o.amount ?? '').trim().startsWith('-');
    const magStr = String(o.amount ?? '').replace(/^-/, '');
    const mode = o.mode || 'pct';
    const unitShort = mode === 'abs' ? (sel?.unit || 'abs') : (mode === 'sigma' ? 'σ' : '%');
    return (
      <Paper key={i} variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: 'background.default' }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            {allSignalDrivers.length > 0 ? (
              <TextField
                select size="small" hiddenLabel value={o.key}
                onChange={(e) => patchOverride(i, { key: e.target.value })}
                sx={{ flexGrow: 1, minWidth: 160 }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) => v || 'Pick a driver…',
                  MenuProps: { PaperProps: { sx: { maxHeight: 420 } } },
                }}
              >
                {signalDrivers.length > 0 && (
                  <ListSubheader disableSticky sx={{ lineHeight: '30px', fontSize: 11 }}>
                    Validated drivers
                  </ListSubheader>
                )}
                {signalDrivers.map(driverMenuItem)}
                {suggestiveDrivers.length > 0 && (
                  <ListSubheader disableSticky sx={{ lineHeight: '30px', fontSize: 11 }}>
                    More signals the model learns from
                  </ListSubheader>
                )}
                {suggestiveDrivers.map(driverMenuItem)}
              </TextField>
            ) : (
              <TextField
                size="small" hiddenLabel value={o.key}
                onChange={(e) => patchOverride(i, { key: e.target.value })}
                placeholder="type a driver key" sx={{ flexGrow: 1, minWidth: 160 }}
              />
            )}
            <Tooltip arrow title="Remove">
              <IconButton size="small" onClick={() => removeOverride(i)} aria-label="Remove driver">
                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <ToggleButtonGroup
              exclusive size="small" value={neg ? 'down' : 'up'}
              onChange={(e, v) => v && setOverrideDir(i, o, v === 'down' ? -1 : 1)}
              sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.4, textTransform: 'none', fontSize: 12, gap: 0.4 } }}
            >
              <ToggleButton value="up" aria-label="Increase"
                sx={(t) => ({ '&.Mui-selected': { color: t.palette.success.main } })}>
                <TrendingUpIcon sx={{ fontSize: 15 }} /> up
              </ToggleButton>
              <ToggleButton value="down" aria-label="Decrease"
                sx={(t) => ({ '&.Mui-selected': { color: t.palette.error.main } })}>
                <TrendingDownIcon sx={{ fontSize: 15 }} /> down
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">by</Typography>
            <TextField
              size="small" hiddenLabel type="number" value={magStr}
              onChange={(e) => setOverrideMag(i, o, e.target.value)}
              inputProps={{ min: 0, 'aria-label': 'Amount' }} sx={{ width: 72 }}
            />
            <TextField
              select size="small" hiddenLabel value={mode}
              onChange={(e) => patchOverride(i, { mode: e.target.value })}
              sx={{ width: 88 }}
              SelectProps={{ renderValue: () => unitShort }}
            >
              {UNIT_OPTIONS.map((u) => (
                <MenuItem key={u.value} value={u.value}>
                  {/* The abs option names the signal's real unit when known:
                      "absolute units ($/MWh)". */}
                  {u.value === 'abs' && sel?.unit ? `${u.label} (${sel.unit})` : u.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select size="small" hiddenLabel value={o.shape || 'step'}
              onChange={(e) => patchOverride(i, { shape: e.target.value })}
              sx={{ width: 136 }}
            >
              {SHAPE_OPTIONS.map((s) => (<MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>))}
            </TextField>
          </Stack>

          {sel && (sel.narrative || driverMeta(sel)) && (
            <Typography variant="caption" color="text.secondary">
              {sel.narrative || driverMeta(sel)}
            </Typography>
          )}
        </Stack>
      </Paper>
    );
  };

  // Event card: "⚡ [stream]  [positive | negative]  [1]× typical".
  const renderEvent = (e, i) => {
    const ev = eventDrivers.find((d) => d.key === e.stream);
    return (
      <Paper key={i} variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: 'background.default' }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <BoltIcon sx={{ fontSize: 16, color: 'warning.main', flexShrink: 0 }} />
            {eventStreams.length > 0 ? (
              <TextField
                select size="small" hiddenLabel value={e.stream}
                onChange={(ev2) => patchEvent(i, { stream: ev2.target.value })}
                sx={{ flexGrow: 1, minWidth: 160 }}
                SelectProps={{ displayEmpty: true, renderValue: (v) => v || 'Pick an event…' }}
              >
                {eventStreams.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
              </TextField>
            ) : (
              <TextField
                size="small" hiddenLabel value={e.stream}
                onChange={(ev2) => patchEvent(i, { stream: ev2.target.value })}
                placeholder="event key" sx={{ flexGrow: 1, minWidth: 160 }}
              />
            )}
            <Tooltip arrow title="Remove">
              <IconButton size="small" onClick={() => removeEvent(i)} aria-label="Remove event">
                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <ToggleButtonGroup
              exclusive size="small" value={numOr(e.direction, 1) < 0 ? 'neg' : 'pos'}
              onChange={(ev2, v) => v && patchEvent(i, { direction: v === 'neg' ? -1 : 1 })}
              sx={{ '& .MuiToggleButton-root': { px: 1, py: 0.4, textTransform: 'none', fontSize: 12 } }}
            >
              <ToggleButton value="pos" aria-label="Positive shock"
                sx={(t) => ({ '&.Mui-selected': { color: t.palette.success.main } })}>
                positive
              </ToggleButton>
              <ToggleButton value="neg" aria-label="Negative shock"
                sx={(t) => ({ '&.Mui-selected': { color: t.palette.error.main } })}>
                negative
              </ToggleButton>
            </ToggleButtonGroup>
            <TextField
              size="small" hiddenLabel type="number" value={e.magnitude}
              onChange={(ev2) => patchEvent(i, { magnitude: ev2.target.value })}
              inputProps={{ step: 0.5, min: 0.1, max: 5, 'aria-label': 'Intensity' }} sx={{ width: 72 }}
            />
            <Typography variant="caption" color="text.secondary">× a typical event</Typography>
            {/* WHEN the event hits inside the horizon (0 = right now). The
                engine applies the measured response curve from that step on. */}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>at step</Typography>
            <Tooltip arrow title="Which forecast step the event hits: 0 = immediately, 5 = five steps into the horizon. The response curve starts there.">
              <TextField
                size="small" hiddenLabel type="number"
                value={e.at_offset_bins ?? 0}
                onChange={(ev2) => patchEvent(i, { at_offset_bins: ev2.target.value })}
                inputProps={{ step: 1, min: 0, 'aria-label': 'Timing (forecast step)' }}
                sx={{ width: 64 }}
              />
            </Tooltip>
          </Stack>
          {ev && driverMeta(ev) && (
            <Typography variant="caption" color="text.secondary">{driverMeta(ev)}</Typography>
          )}
        </Stack>
      </Paper>
    );
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
        <Stack spacing={1.75}>
          {/* Hero: the natural-language prompt */}
          <Stack spacing={1}>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                Simulate a what-if
              </Typography>
              <Tooltip arrow title="Describe a change to one of this goal's drivers and we re-run the live forecast model with that change applied, so you can compare the simulated path against the baseline.">
                <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
              </Tooltip>
            </Stack>
            <NlPrompt
              value={nlText}
              onChange={setNlText}
              onSubmit={() => parse(nlText)}
              isLoading={isParsing}
              placeholder={placeholder}
            />
            {suggestions.length > 0 && (
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                {suggestions.map((s) => (
                  <ExampleChip
                    key={s.label}
                    example={s}
                    disabled={isParsing}
                    onClick={() => onSuggestion(s.prompt)}
                  />
                ))}
              </Stack>
            )}
          </Stack>

          {/* Minimal-first: a quiet link to the manual editor */}
          {!showManual && (
            <Box>
              <Button size="small" color="inherit" startIcon={<TuneIcon fontSize="small" />}
                onClick={openManual} sx={{ color: 'text.secondary', textTransform: 'none' }}>
                Build it manually instead
              </Button>
            </Box>
          )}

          <Collapse in={showManual} unmountOnExit>
            <Stack spacing={1.25}>
              <Divider />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <TuneIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Build it manually</Typography>
                </Stack>
                <Tooltip arrow title="Clear & hide">
                  <IconButton size="small" onClick={closeManual} aria-label="Hide manual editor">
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>

              {/* The cards themselves say what they are (↑/↓ toggle = a
                  driver move, ⚡ = an event shock) — no shouting section
                  headers or explainer paragraphs needed. */}
              {overrides.map(renderOverride)}
              {events.map(renderEvent)}

              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small" startIcon={<AddIcon sx={{ fontSize: 15 }} />} onClick={addOverride}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  Add driver
                </Button>
                {eventStreams.length > 0 && (
                  <Tooltip arrow title="Overlay a shock using this workspace's measured event response.">
                    <Button
                      size="small" startIcon={<BoltIcon sx={{ fontSize: 15 }} />} onClick={addEvent}
                      sx={{ textTransform: 'none', color: 'text.secondary' }}
                    >
                      Add event
                    </Button>
                  </Tooltip>
                )}
              </Stack>

              <TextField
                size="small" label="Name this scenario (optional)" value={name}
                onChange={(e) => setName(e.target.value)} placeholder="auto-named from your changes" fullWidth
              />
            </Stack>
          </Collapse>

          {/* Live summary */}
          {clauses.length > 0 && (
            <Paper variant="outlined" sx={(t) => ({
              p: 1.25, borderRadius: 2,
              bgcolor: alpha(t.palette.primary.main, 0.06),
              borderColor: alpha(t.palette.primary.main, 0.25),
            })}>
              <Typography variant="caption" color="text.secondary">This simulation will apply:</Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {clauses.map((c, idx) => (
                  <Chip key={idx} size="small" color="primary" variant="outlined" label={c} />
                ))}
              </Stack>
            </Paper>
          )}

          {(showManual || clauses.length > 0) && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CMButton startIcon={<PlayArrowIcon />} disabled={!canRun} onClick={run}>
                {isRunning ? 'Simulating…' : 'Run simulation'}
              </CMButton>
              <Typography variant="caption" color="text.secondary">
                {creditCost != null
                  ? `${creditCost} credits per run · saved scenarios are free to view`
                  : 'Saved scenarios are free to view'}
              </Typography>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ScenarioBuilder;
